/**
 * US FDA Supplement Facts engine — 21 CFR 101.36.
 *
 * The companion to the Nutrition Facts engine (`panel.ts` / 21 CFR 101.9). A Supplement Facts
 * panel differs structurally enough that it gets its own model + assembler, but it REUSES the
 * shared, legally load-bearing primitives:
 *   - `roundToIncrement` (FDA half-away-from-zero rounding),
 *   - `displayAmount("energy", …)` for the calories field (21 CFR 101.9(c)(1) R_CAL — shared by
 *     both panels per §2.3 of notes/COMPLIANCE-ENGINE-SPEC.md).
 *
 * Key Supplement-Facts rules implemented (notes/COMPLIANCE-ENGINE-SPEC.md §2; eCFR 101.36):
 *  - Title "Supplement Facts"; serving stated in **dosage form** + "Servings Per Container". (§2.1)
 *  - "Amount Per Serving" heading; per-ingredient **Amount Per Serving** + **% Daily Value**. (§2.3)
 *  - Dietary ingredients WITH an established DV are listed ABOVE a heavy divider bar (with a %DV);
 *    "other dietary ingredients" WITHOUT a DV are listed BELOW it, each carrying a **†** that maps
 *    to the footnote "Daily Value not established." (§2.1, §2.6)
 *  - **Proprietary blends**: a named blend with a single total weight, sub-ingredients listed
 *    indented + descending by weight WITHOUT individual amounts; the blend carries the †. (§2.5)
 *  - "Other ingredients:" line (non-dietary excipients) below the panel.
 *  - %DV to the nearest whole %; a DV-bearing amount that rounds below 1% → "Less than 1%". (§2.3)
 *
 * Pure & deterministic — no Shopify / React / fs / Date / Math.random.
 *
 * SCOPE / HONESTY NOTES (carried forward from the NF engine's documented limits):
 *  - The established DV for each DV-bearing ingredient is supplied per-ingredient (`dv`), in the
 *    same unit as `amount`. This keeps the engine faithful for the full open set of supplement
 *    vitamins/minerals/botanicals without hard-coding an RDI/DRV table the repo has not verified
 *    beyond the four Nutrition-Facts micros. (Callers that map an ingredient to a known
 *    Nutrition-Facts nutrient can look its DV up via `dailyValue` and pass it here.)
 *  - Vitamin/mineral/botanical amounts use the FDA-sample significance style (whole units ≥10; one
 *    decimal below) — identical to `displayMicroAmount` and carrying the SAME NEEDS-VERIFICATION
 *    caveat (the reg ties micro precision to "levels of significance", not a fixed grid).
 *  - MACRO-flagged ingredients (`macro:true`) instead use the 101.9(c) gram increments that
 *    21 CFR 101.36(b)(2)(i) incorporates by reference — see `displayMacroAmount`. (Previously these
 *    silently fell through to the significance style; the "flag for the merchant" mitigation that
 *    was claimed here never actually fired.)
 */
import { displayAmount, roundToIncrement } from "./rounding";
import type { ValidationResult } from "./validate";
import type { Market, NutrientId } from "./types";
// Runtime-safe cycle: core-nutrients imports displaySupplementPercentDV from here, but neither
// module references the other at load time — only inside functions — so init order is fine.
import { CORE_NUTRIENT_BY_ID, coreNutrientPercentDV, displayCoreNutrientAmount } from "./core-nutrients";

/** Entry/display unit for a supplement dietary ingredient amount. */
export type SupplementUnit = "g" | "mg" | "mcg";

/** A single dietary ingredient line (vitamin / mineral / botanical / other). */
export interface SupplementIngredient {
  /**
   * Common name — for botanicals include the part of plant and (when required) the Latin
   * binomial in parentheses, e.g. "Echinacea (root)" / "Turmeric (Curcuma longa, root)".
   */
  name: string;
  /** Quantitative amount of the dietary ingredient itself (NOT the source-salt weight). */
  amount: number;
  unit: SupplementUnit;
  /**
   * Established Daily Value, in the SAME unit as `amount`. `null`/omitted ⇒ no DV established —
   * the ingredient is listed below the divider bar and carries the † dagger.
   */
  dv?: number | null;
  /** Indent level (1 = a sub-row). Default 0. */
  indent?: 0 | 1;
  /** Bold the row name. Default false. */
  bold?: boolean;
  /** Macro-type dietary ingredient (fat/carbohydrate/protein…). Drives the 2,000-cal footnote. */
  macro?: boolean;
  /**
   * When set, this row is an FDA core/macro nutrient (Total Fat … Protein). The engine then rounds
   * via the nutrient's real 101.9(c) rule (`displayCoreNutrientAmount`), derives %DV from the DV
   * table (unless `dv` overrides), renders it in the top group in canonical order, and derives
   * indent/bold from `CORE_NUTRIENTS`. The caller's `name`/`unit` are superseded by the catalog's
   * canonical values. Preferred over the legacy `macro` flag for the ten core nutrients.
   */
  nutrientId?: NutrientId;
  /** Marks the ingredient as a botanical so the validator can require its part of plant. */
  botanical?: boolean;
}

/** A proprietary blend: one total weight + sub-ingredients without individual amounts (§2.5). */
export interface ProprietaryBlend {
  /** Blend name, e.g. "Energy Blend". Defaults to "Proprietary Blend" when blank. */
  name: string;
  /** Total blend weight (mandatory). */
  amount: number;
  unit: SupplementUnit;
  /** Sub-ingredients, descending by predominance, listed WITHOUT individual amounts. */
  subIngredients: string[];
}

/** A panel line — either a standalone dietary ingredient or a proprietary blend. */
export type SupplementLine =
  | ({ kind: "ingredient" } & SupplementIngredient)
  | ({ kind: "blend" } & ProprietaryBlend);

/** Serving descriptor for a supplement (21 CFR 101.36(b)(2)(i)). */
export interface SupplementServingInfo {
  /** The dosage-form serving description, e.g. "2 Capsules", "1 Softgel", "1 Teaspoonful (5 mL)". */
  servingSize: string;
  servingsPerContainer: number | "varied";
  /** Heading override; defaults to "Amount Per Serving" (e.g. "Each Tablet Contains"). */
  amountHeading?: string;
}

/** Everything the engine needs to build one Supplement Facts panel. */
export interface SupplementCalcInput {
  market: Market;
  serving: SupplementServingInfo;
  /** Optional calories per serving (R_CAL rounded). */
  calories?: number;
  /** Ordered dietary ingredients + blends (merchant-controlled order; descending convention). */
  ingredients: SupplementLine[];
  /** Non-dietary "Other ingredients:" list (capsule shell, fillers, flow agents…). */
  otherIngredients?: string[];
}

/** One rendered row of a Supplement Facts panel. */
export interface SupplementRow {
  /** "ingredient" = standalone dietary ingredient; "blend" = blend header; "sub" = blend member. */
  kind: "ingredient" | "blend" | "sub";
  name: string;
  /** Amount + unit ("90 mg"); empty for blend sub-ingredient rows. */
  amountStr: string;
  /** "%DV" string ("10%" / "Less than 1%"), the "†" dagger, or null (sub rows / no column). */
  pdvStr: string | null;
  indent: 0 | 1 | 2;
  bold: boolean;
}

/** The fully-assembled, render-ready Supplement Facts panel. */
export interface SupplementPanelModel {
  kind: "supplement_facts";
  market: Market;
  title: "Supplement Facts";
  /** "Serving Size 2 Capsules". */
  servingSizeLine: string;
  /** "Servings Per Container 30". */
  servingsLine: string;
  /** "Amount Per Serving". */
  amountHeading: string;
  /** Calories display string ("5"), or null when calories are not declared. */
  calories: string | null;
  /** DV-bearing dietary ingredients (above the divider bar). */
  dvRows: SupplementRow[];
  /** No-DV ingredients + proprietary blends (below the divider bar). */
  noDvRows: SupplementRow[];
  /** Draw the heavy divider bar between the two groups (only when BOTH are present). */
  showDivider: boolean;
  /** "Other ingredients: …" line, or null. */
  otherIngredientsLine: string | null;
  footnotes: string[];
}

const DAGGER = "†"; // †

const DV_NOT_ESTABLISHED_FOOTNOTE = `${DAGGER} Daily Value not established.`;
const TWO_THOUSAND_CAL_FOOTNOTE =
  "Percent Daily Values are based on a 2,000 calorie diet.";

/**
 * FDA-sample-style quantitative amount for a supplement dietary ingredient: whole units at/above
 * 10, one decimal below 10 (trailing ".0" trimmed), with the unit appended — e.g. "900 mcg",
 * "2.5 mg". Mirrors `displayMicroAmount` and carries the same documented NEEDS-VERIFICATION caveat.
 */
export function displaySupplementAmount(amount: number, unit: SupplementUnit): string {
  const rounded =
    Math.abs(amount) >= 10 ? Math.round(amount) : Math.round(amount * 10) / 10;
  const num = Number(rounded.toFixed(1)).toString(); // drops a trailing ".0"
  return `${num} ${unit}`;
}

/** Convert a supplement entry unit to grams (macros are declared/rounded in grams). */
const SUPPLEMENT_UNIT_TO_GRAMS: Record<SupplementUnit, number> = {
  g: 1,
  mg: 1e-3,
  mcg: 1e-6,
};

/**
 * FDA-correct display amount for a MACRO-type dietary ingredient (carbohydrate, dietary fiber,
 * total/added sugars, protein…). 21 CFR 101.36(b)(2)(i) requires these to follow the 101.9(c)
 * GRAM increments — not the "significance" style used for vitamins/minerals/botanicals. Reuses the
 * Nutrition Facts carbohydrate/protein rule (`displayAmount` → R_CARB): `<0.5 g` → "0 g";
 * `0.5–<1 g` → "less than 1 g"; `≥1 g` → nearest whole gram. Result always carries the gram unit.
 *
 * This legacy carb-grid helper serves the `macro:true` flag path. The ten FDA core nutrients
 * (incl. fat, which needs the 0.5 g R_FAT grid) go through the `nutrientId` path instead
 * (`displayCoreNutrientAmount`), which rounds by each nutrient's real rule.
 */
export function displayMacroAmount(amount: number, unit: SupplementUnit): string {
  const grams = amount * SUPPLEMENT_UNIT_TO_GRAMS[unit];
  // R_CARB returns "0" | "less than 1 g" | "<n>"; the "less than 1 g" case is gram-final already;
  // a bare number needs the gram unit appended.
  const s = displayAmount("carb_total", "US", grams);
  return s.endsWith(" g") ? s : `${s} g`;
}

/**
 * %DV string for a DV-bearing supplement ingredient (21 CFR 101.36(b)(2)(iii)): computed from the
 * UNROUNDED amount, rounded to the nearest whole percent. A positive amount that rounds below 1%
 * renders "Less than 1%". Returns `null` when no DV is established (the row carries the † instead).
 */
export function displaySupplementPercentDV(
  amount: number,
  dv: number | null | undefined,
): string | null {
  if (dv == null || !(dv > 0)) return null;
  const p = (amount / dv) * 100;
  const rounded = roundToIncrement(p, 1);
  if (p > 0 && rounded < 1) return "Less than 1%";
  return `${rounded}%`;
}

function servingSizeLine(input: SupplementCalcInput): string {
  return `Serving Size ${input.serving.servingSize.trim()}`;
}

function servingsLine(input: SupplementCalcInput): string {
  const spc = input.serving.servingsPerContainer;
  const value = spc === "varied" || spc == null ? "varied" : String(spc);
  return `Servings Per Container ${value}`;
}

/**
 * Assemble a complete US Supplement Facts `SupplementPanelModel` from a `SupplementCalcInput`,
 * plus the validation result. Pure & deterministic.
 *
 * Partitioning: each dietary ingredient with an established DV goes above the divider bar (with a
 * %DV); no-DV ingredients and proprietary blends go below it (each with the † dagger). The bar is
 * drawn only when BOTH groups are present.
 */
export function buildSupplementPanel(input: SupplementCalcInput): {
  panel: SupplementPanelModel;
  validation: ValidationResult;
} {
  const validation = validateSupplement(input);
  const { market } = input;

  const calories =
    input.calories == null ? null : displayAmount("energy", market, input.calories);

  // Core/macro nutrients (Total Fat … Protein) render FIRST, in canonical FDA order, regardless of
  // the order the merchant added them; collected with their catalog order for a stable sort.
  const coreRows: { row: SupplementRow; order: number }[] = [];
  const dvRows: SupplementRow[] = [];
  const noDvRows: SupplementRow[] = [];
  let anyMacroWithDV = false;

  for (const line of input.ingredients) {
    if (line.kind === "blend") {
      // Proprietary blend: header row (name + total weight) carries the † dagger.
      noDvRows.push({
        kind: "blend",
        name: line.name.trim() || "Proprietary Blend",
        amountStr: displaySupplementAmount(line.amount, line.unit),
        pdvStr: DAGGER,
        indent: 0,
        bold: true,
      });
      // Sub-ingredients: indented, descending, NO individual amount or %DV (§2.5).
      for (const sub of line.subIngredients) {
        noDvRows.push({
          kind: "sub",
          name: sub,
          amountStr: "",
          pdvStr: null,
          indent: 1,
          bold: false,
        });
      }
      continue;
    }

    // Core nutrient: real per-nutrient rounding + auto-%DV, always in the top group (even with a
    // blank %DV — Trans Fat / Total Sugars have no DV but are NOT †/"no established DV" rows), with
    // indent/bold from the catalog. `name`/`unit` from the caller are superseded by the catalog.
    if (line.nutrientId && CORE_NUTRIENT_BY_ID[line.nutrientId]) {
      const def = CORE_NUTRIENT_BY_ID[line.nutrientId]!;
      const isAddedSugars = def.nutrientId === "sugars_added";
      const amt = displayCoreNutrientAmount(def.nutrientId, line.amount);
      const pdv = coreNutrientPercentDV(def.nutrientId, line.amount, line.dv);
      if (pdv != null) anyMacroWithDV = true;
      coreRows.push({
        order: def.order,
        row: {
          kind: "ingredient",
          // Added Sugars embeds its amount in the label ("Includes 3 g Added Sugars", §101.9(c)(6)).
          name: isAddedSugars ? `Includes ${amt} Added Sugars` : def.label,
          amountStr: isAddedSugars ? "" : amt,
          // A core nutrient with no established DV shows a BLANK %DV cell, never the † dagger.
          pdvStr: pdv ?? "",
          indent: def.indent,
          bold: def.indent === 0,
        },
      });
      continue;
    }

    // Legacy `macro:true` ingredients follow the 101.9(c) carb gram grid; everything else uses the
    // vitamin/mineral significance style.
    const amountStr = line.macro
      ? displayMacroAmount(line.amount, line.unit)
      : displaySupplementAmount(line.amount, line.unit);
    const hasDV = line.dv != null && line.dv > 0;
    if (hasDV) {
      if (line.macro) anyMacroWithDV = true;
      dvRows.push({
        kind: "ingredient",
        name: line.name,
        amountStr,
        pdvStr: displaySupplementPercentDV(line.amount, line.dv),
        indent: line.indent ?? 0,
        bold: line.bold ?? false,
      });
    } else {
      noDvRows.push({
        kind: "ingredient",
        name: line.name,
        amountStr,
        pdvStr: DAGGER,
        indent: line.indent ?? 0,
        bold: line.bold ?? false,
      });
    }
  }

  // Core nutrients in canonical FDA order, then the DV-bearing vitamins/minerals — one top group.
  coreRows.sort((a, b) => a.order - b.order);
  const topRows: SupplementRow[] = [...coreRows.map((c) => c.row), ...dvRows];

  const footnotes: string[] = [];
  if (noDvRows.length > 0) footnotes.push(DV_NOT_ESTABLISHED_FOOTNOTE);
  if (anyMacroWithDV) footnotes.push(TWO_THOUSAND_CAL_FOOTNOTE);

  const otherIngredientsLine =
    input.otherIngredients && input.otherIngredients.length > 0
      ? `Other ingredients: ${input.otherIngredients.map((s) => s.trim()).join(", ")}.`
      : null;

  const panel: SupplementPanelModel = {
    kind: "supplement_facts",
    market,
    title: "Supplement Facts",
    servingSizeLine: servingSizeLine(input),
    servingsLine: servingsLine(input),
    amountHeading: input.serving.amountHeading?.trim() || "Amount Per Serving",
    calories,
    dvRows: topRows,
    noDvRows,
    showDivider: topRows.length > 0 && noDvRows.length > 0,
    otherIngredientsLine,
    footnotes,
  };

  return { panel, validation };
}

/**
 * Validate a US Supplement Facts input against the 21 CFR 101.36 required-field rules
 * (notes/COMPLIANCE-ENGINE-SPEC.md §2.7 / §8). Errors block generation; warnings are advisory.
 *
 * ERRORs: missing dosage-form serving size; missing/non-positive/non-finite servings per
 * container; nothing declared (no ingredient and no calories); a blank ingredient name; a
 * zero-level / non-finite declared dietary ingredient amount (101.36 — must not declare); a
 * proprietary blend without a positive, finite total weight. A blank blend NAME is NOT an error —
 * 101.36(c) allows the generic "Proprietary Blend" heading, which the assembler supplies.
 *
 * WARNINGs: a botanical whose name lacks a part-of-plant parenthetical; a proprietary blend with
 * no sub-ingredients listed.
 *
 * Out of v1 scope (honest): full (b)(2)(i)(B) ingredient-ordering enforcement, source-salt-weight
 * detection, "protein declared when only amino acids" detection — these need data the model does
 * not carry and are flagged for the merchant rather than silently faked.
 */
export function validateSupplement(input: SupplementCalcInput): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  if (input.market !== "US") {
    errors.push(`market ${input.market} Supplement Facts validation not implemented`);
    return { errors, warnings };
  }

  const serving = input.serving;
  if (!serving || !serving.servingSize || serving.servingSize.trim() === "") {
    errors.push('Serving size is required (state it in dosage form, e.g. "2 Capsules").');
  }
  if (
    !serving ||
    serving.servingsPerContainer == null ||
    (typeof serving.servingsPerContainer === "number" &&
      !(serving.servingsPerContainer > 0))
  ) {
    errors.push("Servings per container is required.");
  }

  const ingredients = input.ingredients ?? [];
  if (ingredients.length === 0 && input.calories == null) {
    errors.push("Declare at least one dietary ingredient (or calories).");
  }

  // A core nutrient may only be declared once (two "Total Fat" rows = a non-compliant panel).
  const seenCore = new Set<string>();

  for (const line of ingredients) {
    if (line.kind === "blend") {
      // A blank blend name is legal: 21 CFR 101.36(c) permits the generic heading "Proprietary
      // Blend", which `buildSupplementPanel` supplies as the default — so don't error on it.
      if (!(Number.isFinite(line.amount) && line.amount > 0)) {
        errors.push(
          `${line.name?.trim() || "Proprietary blend"}: a proprietary blend must declare its total weight.`,
        );
      }
      if (!line.subIngredients || line.subIngredients.length === 0) {
        warnings.push(
          `${line.name?.trim() || "Proprietary blend"}: list the blend's sub-ingredients in descending order by weight.`,
        );
      }
      continue;
    }

    // A core/macro nutrient (Total Fat … Protein) may legitimately be zero (Trans Fat 0 g, Added
    // Sugars 0 g are the most common declarations) — only reject non-finite/negative. It also gets
    // its canonical catalog name, so a blank `name` is fine.
    const core = line.nutrientId ? CORE_NUTRIENT_BY_ID[line.nutrientId] : undefined;
    if (core) {
      if (!(Number.isFinite(line.amount) && line.amount >= 0)) {
        errors.push(`${core.label}: enter an amount of zero or more.`);
      }
      if (seenCore.has(line.nutrientId!)) {
        errors.push(`${core.label} is declared more than once — remove the duplicate row.`);
      }
      seenCore.add(line.nutrientId!);
      continue;
    }

    const label = line.name?.trim();
    if (!label) {
      errors.push("Every dietary ingredient needs a name.");
      continue;
    }
    if (!(Number.isFinite(line.amount) && line.amount > 0)) {
      errors.push(
        `${label}: a declared dietary ingredient must have an amount greater than zero (do not declare zero-level ingredients).`,
      );
    }
    if (line.botanical && !/\(.+\)/.test(line.name)) {
      warnings.push(
        `${label}: list the part of the plant (e.g. "root", "leaf") — and the Latin binomial when the common name isn't in Herbs of Commerce.`,
      );
    }
  }

  return { errors, warnings };
}
